/**
 * WorkOrder + WorkOrderAssignment + StepRecord + WeightRecord + Incident schemas.
 *
 * Invariants enforced here by refinement:
 *  - merma_reason MANDATORY when merma_quantity > 0 (merma always carries a reason).
 *  - cancellation_reason MANDATORY when status='cancelled'.
 *  - an assignment targets exactly ONE of user / section (XOR by target_type).
 */

import { z } from 'zod';
import {
  WORK_ORDER_TYPES,
  WORK_ORDER_STATUSES,
  ASSIGNMENT_TARGET_TYPES,
  RECIPE_STEP_CONTROL_TYPES,
  INCIDENT_TYPES,
  INCIDENT_SEVERITIES,
  WEIGHT_SOURCES,
} from '../enums/index.js';
import { MERMA_REASON_CODES } from '../constants/index.js';
import {
  uuid,
  nonEmptyString,
  timestamptz,
  dateString,
  quantity,
  nonNegativeQuantity,
  percent,
  softDeleteFields,
  timestampFields,
  syncFields,
} from './common.js';

// ── WorkOrder ─────────────────────────────────────────────────────────────────

export const WorkOrder = z
  .object({
    id: uuid,
    tenant_id: uuid,
    work_order_number: nonEmptyString,
    work_order_type: z.enum(WORK_ORDER_TYPES),
    recipe_id: uuid,
    recipe_version_id: uuid,
    output_article_id: uuid,
    planned_quantity: nonNegativeQuantity,
    planned_unit_id: uuid,
    real_quantity: nonNegativeQuantity.nullable(),
    merma_quantity: quantity.nullable(),
    merma_pct: percent.nullable(),
    merma_reason: z.enum(MERMA_REASON_CODES).nullable(),
    status: z.enum(WORK_ORDER_STATUSES),
    production_date: dateString,
    shift_id: uuid.nullable(),
    source_finished_lot_id: uuid.nullable(),
    published_at: timestamptz.nullable(),
    started_at: timestamptz.nullable(),
    completed_at: timestamptz.nullable(),
    cancellation_reason: z.string().trim().min(1).nullable(),
    closeout_note: z.string().trim().min(1).nullable(),
    created_by: uuid.nullable(),
    ...syncFields,
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict()
  .refine(
    (wo) =>
      wo.merma_quantity === null || wo.merma_quantity <= 0 || wo.merma_reason !== null,
    {
      message: 'merma_reason is required when merma_quantity > 0',
      path: ['merma_reason'],
    },
  )
  .refine((wo) => wo.status !== 'cancelled' || wo.cancellation_reason !== null, {
    message: 'cancellation_reason is required when status is "cancelled"',
    path: ['cancellation_reason'],
  });
export type WorkOrder = z.infer<typeof WorkOrder>;

/**
 * Create an OT. The server generates work_order_number (never manual) and PINS the active
 * recipe version at creation (immutability). The client provides the planning target;
 * tenant_id is injected. `recipe_version_id` is optional from the client — the server
 * resolves the active version if absent and always re-pins it authoritatively.
 */
export const WorkOrderCreateInput = z
  .object({
    work_order_type: z.enum(WORK_ORDER_TYPES).default('production'),
    recipe_id: uuid,
    output_article_id: uuid,
    planned_quantity: nonNegativeQuantity,
    planned_unit_id: uuid,
    production_date: dateString,
    shift_id: uuid.optional(),
    source_finished_lot_id: uuid.optional(),
    publish: z.boolean().default(false),
    client_uuid: uuid.optional(),
  })
  .strict();
export type WorkOrderCreateInput = z.infer<typeof WorkOrderCreateInput>;

/**
 * OT close payload. Records real quantity; if it implies merma > 0, a merma_reason is
 * MANDATORY. The server computes merma_quantity/pct (domain/merma.ts) and generates the
 * finished lot + lot number (never manual).
 */
export const WorkOrderCloseInput = z
  .object({
    real_quantity: nonNegativeQuantity,
    merma_reason: z.enum(MERMA_REASON_CODES).optional(),
    expiry_date: dateString.optional(),
    closeout_note: z.string().trim().min(1).optional(),
    client_uuid: uuid.optional(),
  })
  .strict();
export type WorkOrderCloseInput = z.infer<typeof WorkOrderCloseInput>;

/** OT cancellation payload — written reason MANDATORY. */
export const WorkOrderCancelInput = z
  .object({
    cancellation_reason: z.string().trim().min(1),
  })
  .strict();
export type WorkOrderCancelInput = z.infer<typeof WorkOrderCancelInput>;

// ── WorkOrderAssignment ───────────────────────────────────────────────────────

export const WorkOrderAssignment = z
  .object({
    id: uuid,
    tenant_id: uuid,
    work_order_id: uuid,
    target_type: z.enum(ASSIGNMENT_TARGET_TYPES),
    user_id: uuid.nullable(),
    section_id: uuid.nullable(),
    created_at: timestamptz,
    created_by: uuid.nullable(),
  })
  .strict()
  .refine(
    (a) =>
      a.target_type === 'user'
        ? a.user_id !== null && a.section_id === null
        : a.section_id !== null && a.user_id === null,
    { message: 'assignment must target exactly one of user / section (matching target_type)' },
  );
export type WorkOrderAssignment = z.infer<typeof WorkOrderAssignment>;

export const WorkOrderAssignmentInput = z
  .discriminatedUnion('target_type', [
    z.object({ target_type: z.literal('user'), user_id: uuid }).strict(),
    z.object({ target_type: z.literal('section'), section_id: uuid }).strict(),
  ]);
export type WorkOrderAssignmentInput = z.infer<typeof WorkOrderAssignmentInput>;

// ── WorkOrderResource (the machine of a work order, OBR-45) ──────────────────────

/**
 * One capacity the OT's recipe asks for («1 × Amassadora») and the machine chosen
 * for it when planning. The lines are born with the OT (DB trigger, copied from the
 * fixed recipe version); the only thing planning changes is `resource_id`, which the
 * server checks is a machine of that type. Null = not chosen yet.
 */
export const WorkOrderResource = z
  .object({
    id: uuid,
    tenant_id: uuid,
    work_order_id: uuid,
    /** The line of the pinned recipe version it comes from. */
    recipe_version_resource_id: uuid.nullable(),
    resource_type_id: uuid,
    resource_id: uuid.nullable(),
    quantity: quantity.positive(),
    duration_minutes: z.number().int().nonnegative().nullable(),
    sort_order: z.number().int(),
    created_at: timestamptz,
    updated_at: timestamptz,
  })
  .strict();
export type WorkOrderResource = z.infer<typeof WorkOrderResource>;

/** Choose (or clear) the machine of one OT resource line. */
export const WorkOrderResourceAssignInput = z
  .object({
    resource_id: uuid.nullable(),
  })
  .strict();
export type WorkOrderResourceAssignInput = z.infer<typeof WorkOrderResourceAssignInput>;

// ── StepRecord (append-only) ──────────────────────────────────────────────────

export const StepRecord = z
  .object({
    id: uuid,
    tenant_id: uuid,
    work_order_step_id: uuid,
    work_order_id: uuid,
    control_type: z.enum(RECIPE_STEP_CONTROL_TYPES),
    measured_value: quantity.nullable(),
    measured_unit: z.string().trim().min(1).nullable(),
    note: z.string().trim().min(1).nullable(),
    recorded_by: uuid,
    recorded_at: timestamptz,
    ...syncFields,
    created_at: timestamptz,
  })
  .strict();
export type StepRecord = z.infer<typeof StepRecord>;

/**
 * Append-only step record from the device. `recorded_at` is the operator device clock
 * (passed in); the server keeps an authoritative immutable created_at.
 */
export const StepRecordInput = z
  .object({
    work_order_step_id: uuid,
    work_order_id: uuid,
    control_type: z.enum(RECIPE_STEP_CONTROL_TYPES),
    measured_value: quantity.optional(),
    measured_unit: z.string().trim().min(1).optional(),
    note: z.string().trim().min(1).optional(),
    recorded_at: timestamptz,
    client_uuid: uuid.optional(),
  })
  .strict();
export type StepRecordInput = z.infer<typeof StepRecordInput>;

// ── WeightRecord (append-only) ────────────────────────────────────────────────

export const WeightRecord = z
  .object({
    id: uuid,
    tenant_id: uuid,
    work_order_id: uuid,
    work_order_step_id: uuid.nullable(),
    article_id: uuid,
    supplier_lot_id: uuid.nullable(),
    planned_quantity: nonNegativeQuantity,
    real_quantity: nonNegativeQuantity,
    unit_id: uuid,
    deviation_pct: percent.nullable(),
    source: z.enum(WEIGHT_SOURCES),
    scale_device_id: uuid.nullable(),
    recorded_by: uuid,
    recorded_at: timestamptz,
    ...syncFields,
    created_at: timestamptz,
  })
  .strict();
export type WeightRecord = z.infer<typeof WeightRecord>;

export const WeightRecordInput = z
  .object({
    work_order_id: uuid,
    work_order_step_id: uuid.optional(),
    article_id: uuid,
    supplier_lot_id: uuid.optional(),
    planned_quantity: nonNegativeQuantity,
    real_quantity: nonNegativeQuantity,
    unit_id: uuid,
    source: z.enum(WEIGHT_SOURCES).default('manual'),
    scale_device_id: uuid.optional(),
    recorded_at: timestamptz,
    client_uuid: uuid.optional(),
  })
  .strict();
export type WeightRecordInput = z.infer<typeof WeightRecordInput>;

// ── Incident (append-only) ────────────────────────────────────────────────────

export const Incident = z
  .object({
    id: uuid,
    tenant_id: uuid,
    work_order_id: uuid.nullable(),
    work_order_step_id: uuid.nullable(),
    type: z.enum(INCIDENT_TYPES),
    severity: z.enum(INCIDENT_SEVERITIES),
    comment: z.string().trim().min(1).nullable(),
    photo_url: z.string().trim().min(1).nullable(),
    reported_by: uuid,
    reported_at: timestamptz,
    resolved_at: timestamptz.nullable(),
    resolved_by: uuid.nullable(),
    ...syncFields,
    created_at: timestamptz,
  })
  .strict();
export type Incident = z.infer<typeof Incident>;

/** Operator-logged incident (max 3 mandatory fields: type, severity, comment). */
export const IncidentInput = z
  .object({
    work_order_id: uuid.optional(),
    work_order_step_id: uuid.optional(),
    type: z.enum(INCIDENT_TYPES),
    severity: z.enum(INCIDENT_SEVERITIES),
    comment: z.string().trim().min(1).optional(),
    photo_url: z.string().trim().min(1).optional(),
    reported_at: timestamptz,
    client_uuid: uuid.optional(),
  })
  .strict();
export type IncidentInput = z.infer<typeof IncidentInput>;
